import { BiDashboardGhost } from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { BiPreviewCardState } from '@euler/model/bi/change-set';
import { BiCardView, BiDashboardView } from '@euler/model/bi/dashboard';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import { BiRenderCard, DashboardGrid } from '../grid/DashboardGrid';
import { GhostBanner } from './GhostBanner';
import { GhostCardFrame } from './GhostCardFrame';
import { GhostDraftCard } from './GhostDraftCard';
import { biGhostCards } from './functions/biGhostCards';
import { useBiGhostDrafts } from './functions/useBiGhostDrafts';

/**
 * The dashboard as a pending change set would leave it, each changed card
 * marked, over a banner to apply or discard it.
 */
export const GhostDashboard = memo(
  ({
    dashboard,
    ghost,
    busy,
    renderCard,
    onApply,
    onDiscard,
  }: {
    dashboard: BiDashboardView;
    ghost: BiDashboardGhost;
    busy: boolean;
    /** A card as the dashboard draws it, without its actions. */
    renderCard: BiRenderCard;
    onApply: () => void;
    onDiscard: () => void;
  }) => {
    const drafts = useBiGhostDrafts(ghost);
    const { layout, cards, states, removed } = useMemo(
      () => biGhostCards(dashboard, ghost, drafts),
      [dashboard, ghost, drafts],
    );
    const renderGhost = useCallback(
      (
        card: BiCardView,
        height: number | undefined,
        grip?: ReactNode,
        footer?: ReactNode,
        onFitHeight?: (px: number) => void,
      ) => {
        const state = states.get(card.id);
        const draft =
          state === BiPreviewCardState.Added && card.sourceDraftId
            ? drafts.get(card.sourceDraftId)
            : undefined;
        return (
          <GhostCardFrame state={state}>
            {draft ? (
              <GhostDraftCard
                draft={draft}
                title={card.title}
                height={height}
                palette={dashboard.palette}
                onFitHeight={onFitHeight}
              />
            ) : (
              renderCard(card, height, grip, footer, onFitHeight)
            )}
          </GhostCardFrame>
        );
      },
      [states, drafts, renderCard, dashboard.palette],
    );
    return (
      <>
        <GhostBanner
          summary={ghost.summary}
          removed={removed}
          busy={busy}
          onApply={onApply}
          onDiscard={onDiscard}
        />
        <DashboardGrid
          layout={layout}
          revision={dashboard.revision}
          cards={cards}
          renderCard={renderGhost}
        />
      </>
    );
  },
);
